import React, { useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import {
  sendVerificationEmail,
  hasCurrentUserErrors,
  setSeenNotifications,
} from '../../ducks/user.duck';
// FairwayBid: was an diesem Browser schon im Posteingang stand.
import { holeGesehen, ungeleseneAnzahl } from '../../fairwaybid/gesehen';
import { logout, authenticationInProgress } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

/**
 * Topbar container component, which is connected to Redux Store.
 * @component
 * @param {Object} props
 * @param {number} props.notificationCount number of notifications
 * @param {Function} props.onLogout logout function
 * @param {Function} props.onManageDisableScrolling manage disable scrolling function
 * @param {Function} props.onResendVerificationEmail resend verification email function
 * @param {Object} props.sendVerificationEmailInProgress send verification email in progress
 * @param {Object} props.sendVerificationEmailError send verification email error
 * @param {boolean} props.hasGenericError has generic error
 * @returns {JSX.Element}
 */
export const TopbarContainerComponent = props => {
  const {
    notificationCount = 0,
    notificationIds = [],
    seenNotificationIds = [],
    onSeenNotifications,
    hasGenericError,
    ...rest
  } = props;

  // Der gemerkte Stand kommt erst nach dem Aufbau dazu: localStorage gibt es
  // auf dem Server nicht. Wuerde der Client ihn sofort einrechnen, stuende
  // dort eine andere Zahl als im gelieferten HTML - und die Hydration bricht.
  // So zeigt der erste Bildaufbau die volle Zahl und korrigiert sich sofort.
  useEffect(() => {
    const gemerkt = holeGesehen();
    if (gemerkt.length > 0 && onSeenNotifications) {
      onSeenNotifications(gemerkt);
    }
  }, [onSeenNotifications]);

  // Die IDs sind die genauere Quelle. Fehlen sie - ein alter Zwischenstand,
  // ein Fehler bei der Abfrage -, bleibt es bei der reinen Zahl.
  const ungelesen =
    notificationIds.length > 0
      ? ungeleseneAnzahl(notificationIds, seenNotificationIds)
      : notificationCount;

  return <Topbar notificationCount={ungelesen} showGenericError={hasGenericError} {...rest} />;
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated and isLoggedInAs
  const { isAuthenticated, isLoggedInAs, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserSaleNotificationCount = 0,
    currentUserOrderNotificationCount = 0,
    currentUserNotificationIds = [],
    currentUserSeenNotificationIds = [],
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    notificationCount: currentUserSaleNotificationCount + currentUserOrderNotificationCount,
    notificationIds: currentUserNotificationIds,
    seenNotificationIds: currentUserSeenNotificationIds,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onSeenNotifications: ids => dispatch(setSeenNotifications(ids)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(TopbarContainerComponent);

export default TopbarContainer;
